import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Briefcase, Plus, Pencil, Copy, Trash2, Search, Filter, Save,
  GraduationCap, TrendingUp, ClipboardList, HistoryIcon as HIcon, CheckSquare,
  AlertTriangle, Award, Sparkles, Loader2,
} from "lucide-react";
import { BRL, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const NIVEIS = ["Auxiliar", "Trainee", "Júnior", "Pleno", "Sênior", "Master", "Especialista", "Líder", "Supervisor", "Coordenador", "Gerente", "Diretor"];
const STATUS = { ativo: "Ativo", inativo: "Inativo", em_revisao: "Em revisão" };

const emptyRole = () => ({
  company_id: "", sector_id: null, plan_id: null,
  nome: "", nivel: "Pleno", codigo: "", salario: 0, status: "ativo", observacoes: "",
  requisitos: {}, criterios_promocao: { requer_aprovacao_gestor: true, requer_aprovacao_rh: true, requer_vaga_aprovada: true },
  atividades: {},
});

export default function JobRolesPage() {
  const { companies, companyParam } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin");
  const [list, setList] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(new Set());

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRole());
  const [tab, setTab] = useState("dados");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);

  const generateAiDescription = async () => {
    if (!form.nome?.trim()) {
      toast.error("Informe o nome do cargo antes de gerar com IA");
      return;
    }
    setIaLoading(true);
    try {
      const { data } = await api.post("/openai/cargo-description", {
        cargo: form.nome,
        nivel: form.nivel || undefined,
        setor: form.setor_nome || undefined,
        empresa: form.empresa_nome || undefined,
      });
      const joinList = (arr) => (Array.isArray(arr) && arr.length ? arr.map((x) => `• ${x}`).join("\n") : "");
      setForm((prev) => ({
        ...prev,
        atividades: {
          ...(prev.atividades || {}),
          principais_atividades: data.missao || prev.atividades?.principais_atividades || "",
          responsabilidades: joinList(data.responsabilidades) || prev.atividades?.responsabilidades || "",
          rotinas: prev.atividades?.rotinas || "",
          entregas_esperadas: prev.atividades?.entregas_esperadas || "",
          ferramentas_sistemas: joinList(data.requisitos_tecnicos) || prev.atividades?.ferramentas_sistemas || "",
          relacionamento_setores: joinList(data.competencias_comportamentais) || prev.atividades?.relacionamento_setores || "",
        },
      }));
      toast.success("Descrição gerada com GPT-5.5 — revise antes de salvar");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao gerar descrição");
    } finally {
      setIaLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/job-roles",
        { params: companyParam ? { company_id: companyParam } : {} });
      setList(data);
      setSelected(new Set());
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/sectors", { params: companyParam ? { company_id: companyParam } : {} })
       .then(({ data }) => setSectors(Array.isArray(data) ? data : data.items || []));
    // eslint-disable-next-line
  }, [companyParam]);

  const filtered = useMemo(() => {
    return list.filter(r => {
      if (filterStatus && r.status !== filterStatus) return false;
      if (q) {
        const hay = `${r.nome} ${r.codigo || ""} ${r.sector_name || ""} ${r.nivel || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [list, q, filterStatus]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyRole(), company_id: companyParam || companies[0]?.id || "" });
    setTab("dados");
    setHistory([]);
    setOpen(true);
  };

  const openEdit = async (role) => {
    setEditing(role);
    try {
      const { data } = await api.get(`/job-roles/${role.id}`);
      setForm({
        ...emptyRole(), ...data,
        requisitos: data.requisitos || {},
        criterios_promocao: data.criterios_promocao || {},
        atividades: data.atividades || {},
      });
      setHistory(data.history || []);
    } catch {
      setForm({ ...emptyRole(), ...role });
      setHistory([]);
    }
    setTab("dados");
    setOpen(true);
  };

  const save = async () => {
    if (!form.nome?.trim()) { toast.error("Nome do cargo é obrigatório"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/job-roles/${editing.id}`, form);
        toast.success("Cargo atualizado");
      } else {
        await api.post("/job-roles", form);
        toast.success("Cargo criado");
      }
      setOpen(false);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const duplicateRole = async (role) => {
    try {
      await api.post(`/job-roles/${role.id}/duplicate`);
      toast.success(`"${role.nome}" duplicado`);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const deleteOne = async (role) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${role.nome}"?\n\nEsta ação pode impactar relatórios e estruturas vinculadas.`)) return;
    try {
      const { data } = await api.delete(`/job-roles/${role.id}`);
      if (data.linked_active_employees > 0) {
        toast.warning(`Cargo excluído. ${data.linked_active_employees} colaborador(es) ativo(s) referenciam este cargo pelo nome — revise os cadastros.`);
      } else {
        toast.success("Cargo excluído");
      }
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Excluir ${selected.size} cargo(s) selecionado(s)?\n\nEsta ação pode impactar relatórios e estruturas vinculadas.`)) return;
    try {
      await api.post("/job-roles/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${selected.size} cargo(s) excluído(s)`);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const stats = useMemo(() => ({
    total: list.length,
    ativos: list.filter(r => r.status === "ativo").length,
    em_revisao: list.filter(r => r.status === "em_revisao").length,
    salario_medio: list.length ? list.reduce((s, r) => s + (r.salario || 0), 0) / list.length : 0,
  }), [list]);

  return (
    <div className="space-y-5" data-testid="job-roles-page">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <K label="Total de cargos" value={stats.total} icon={Briefcase} tone="primary" />
        <K label="Ativos" value={stats.ativos} icon={CheckSquare} tone="success" />
        <K label="Em revisão" value={stats.em_revisao} icon={AlertTriangle} tone="warning" />
        <K label="Salário médio" value={BRL(stats.salario_medio)} icon={Award} tone="accent" />
      </div>

      {/* Toolbar */}
      <div className="rounded-lg border border-border bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cargo, código, setor..." className="pl-9" data-testid="jr-search" />
        </div>
        <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="jr-filter-status"><Filter className="h-3.5 w-3.5 mr-1" /><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {canEdit && selected.size > 0 && (
          <Button variant="destructive" onClick={bulkDelete} data-testid="jr-bulk-delete">
            <Trash2 className="h-4 w-4 mr-2" /> Excluir {selected.size}
          </Button>
        )}
        {canEdit && (
          <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="jr-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo cargo
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {canEdit && <TableHead className="w-8"></TableHead>}
              <TableHead>Cargo</TableHead>
              <TableHead>Nível</TableHead>
              <TableHead>Código</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead className="text-right">Salário</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-5" /></TableCell></TableRow>)
              : filtered.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Nenhum cargo encontrado. Crie um cargo manualmente ou publique cargos a partir de um plano em "Tabela Salarial".</TableCell></TableRow>
              : filtered.map(r => (
                <TableRow key={r.id} data-testid={`jr-row-${r.id}`}>
                  {canEdit && <TableCell><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} data-testid={`jr-sel-${r.id}`} /></TableCell>}
                  <TableCell className="font-medium">{r.nome}</TableCell>
                  <TableCell className="text-[12px]"><Badge variant="outline">{r.nivel || "—"}</Badge></TableCell>
                  <TableCell className="font-mono text-[11.5px]">{r.codigo || "—"}</TableCell>
                  <TableCell className="text-[12px]">{r.sector_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{BRL(r.salario || 0)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      r.status === "ativo" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : r.status === "em_revisao" ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-slate-100 text-slate-600"
                    }>{STATUS[r.status] || r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`jr-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => duplicateRole(r)} title="Duplicar" data-testid={`jr-dup-${r.id}`}><Copy className="h-3.5 w-3.5" /></Button>}
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => deleteOne(r)} title="Excluir"><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Role Dialog with tabs */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="jr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#C5A059]" />
              {editing ? `Editar · ${editing.nome}` : "Novo cargo"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-[12px] text-muted-foreground">
                Estrutura de gestão de carreira: dados, requisitos, critérios de promoção e atividades.
              </div>
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-secondary/40 w-full justify-start flex-wrap h-auto">
              <TabsTrigger value="dados" data-testid="jr-tab-dados"><Briefcase className="h-3.5 w-3.5 mr-1.5" /> Dados gerais</TabsTrigger>
              <TabsTrigger value="requisitos" data-testid="jr-tab-req"><GraduationCap className="h-3.5 w-3.5 mr-1.5" /> Requisitos</TabsTrigger>
              <TabsTrigger value="promocao" data-testid="jr-tab-prom"><TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Critérios de promoção</TabsTrigger>
              <TabsTrigger value="atividades" data-testid="jr-tab-ativ"><ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Atividades</TabsTrigger>
              <TabsTrigger value="historico" data-testid="jr-tab-hist"><HIcon className="h-3.5 w-3.5 mr-1.5" /> Histórico</TabsTrigger>
            </TabsList>

            <TabsContent value="dados" className="mt-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider">Nome do cargo *</Label>
                  <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: Mecânico de Colhedora Sênior" data-testid="jr-nome" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Nível</Label>
                  <Select value={form.nivel || ""} onValueChange={(v) => setForm({ ...form, nivel: v })}>
                    <SelectTrigger data-testid="jr-nivel"><SelectValue /></SelectTrigger>
                    <SelectContent>{NIVEIS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Código interno</Label>
                  <Input value={form.codigo || ""} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="Ex.: OFT-MEC-03" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
                  <Select value={form.sector_id || "__none__"} onValueChange={(v) => setForm({ ...form, sector_id: v === "__none__" ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="Nenhum" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nenhum</SelectItem>
                      {sectors.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Salário</Label>
                  <Input type="number" step="0.01" value={form.salario} onChange={(e) => setForm({ ...form, salario: Number(e.target.value) })} data-testid="jr-salario" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="jr-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
                  <Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="requisitos" className="mt-4 space-y-3">
              <SectionHint>Defina os requisitos mínimos para que um colaborador ocupe este cargo.</SectionHint>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Area label="Escolaridade mínima" v={form.requisitos?.escolaridade} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, escolaridade: x } })} />
                <Area label="Experiência mínima" v={form.requisitos?.experiencia_minima} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, experiencia_minima: x } })} />
                <Area label="Cursos obrigatórios" v={form.requisitos?.cursos_obrigatorios} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, cursos_obrigatorios: x } })} rows={3} />
                <Area label="Certificações" v={form.requisitos?.certificacoes} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, certificacoes: x } })} rows={3} />
                <Area label="Conhecimentos técnicos" v={form.requisitos?.conhecimentos_tecnicos} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, conhecimentos_tecnicos: x } })} rows={3} />
                <Area label="Competências comportamentais" v={form.requisitos?.competencias_comportamentais} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, competencias_comportamentais: x } })} rows={3} />
                <Area label="Habilitação / Categoria" v={form.requisitos?.habilitacao} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, habilitacao: x } })} />
                <Area label="Treinamentos internos obrigatórios" v={form.requisitos?.treinamentos_internos} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, treinamentos_internos: x } })} rows={3} />
                <Area label="Outros requisitos" v={form.requisitos?.outros} s={(x) => setForm({ ...form, requisitos: { ...form.requisitos, outros: x } })} full rows={2} />
              </div>
            </TabsContent>

            <TabsContent value="promocao" className="mt-4 space-y-3">
              <SectionHint>Configure os critérios para promoção ao cargo. Cada função pode ter critérios diferentes.</SectionHint>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Area label="Tempo mínimo na função anterior" v={form.criterios_promocao?.tempo_minimo_funcao} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, tempo_minimo_funcao: x } })} placeholder="Ex.: 12 meses" />
                <Area label="Avaliação de desempenho mínima" v={form.criterios_promocao?.avaliacao_desempenho_minima} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, avaliacao_desempenho_minima: x } })} placeholder="Ex.: B ou acima" />
                <Area label="Formação exigida" v={form.criterios_promocao?.formacao_exigida} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, formacao_exigida: x } })} />
                <Area label="Cursos concluídos" v={form.criterios_promocao?.cursos_concluidos} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, cursos_concluidos: x } })} />
                <Area label="Treinamentos internos concluídos" v={form.criterios_promocao?.treinamentos_internos_concluidos} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, treinamentos_internos_concluidos: x } })} />
                <Area label="Competências técnicas exigidas" v={form.criterios_promocao?.competencias_tecnicas} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, competencias_tecnicas: x } })} rows={3} />
                <Area label="Competências comportamentais exigidas" v={form.criterios_promocao?.competencias_comportamentais} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, competencias_comportamentais: x } })} rows={3} />
                <Area label="Outros critérios personalizados" v={form.criterios_promocao?.outros_criterios} s={(x) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, outros_criterios: x } })} rows={2} />
              </div>
              <div className="mt-4 rounded-md border border-border bg-secondary/30 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Aprovações necessárias</div>
                <div className="flex flex-col gap-2">
                  <SwitchRow label="Aprovação do gestor" checked={!!form.criterios_promocao?.requer_aprovacao_gestor} onChange={(v) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, requer_aprovacao_gestor: v } })} />
                  <SwitchRow label="Aprovação do RH" checked={!!form.criterios_promocao?.requer_aprovacao_rh} onChange={(v) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, requer_aprovacao_rh: v } })} />
                  <SwitchRow label="Vaga aprovada no quadro" checked={!!form.criterios_promocao?.requer_vaga_aprovada} onChange={(v) => setForm({ ...form, criterios_promocao: { ...form.criterios_promocao, requer_vaga_aprovada: v } })} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="atividades" className="mt-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <SectionHint>Descreva as atividades, responsabilidades e rotinas da função. Use para recrutamento, avaliação e treinamento.</SectionHint>
                <button
                  type="button"
                  onClick={generateAiDescription}
                  disabled={iaLoading || !canEdit}
                  className="shrink-0 flex items-center gap-1.5 rounded-md bg-[#234935] hover:bg-[#2c5c43] disabled:opacity-50 text-white text-[12.5px] font-medium px-3 py-2 transition-colors"
                  data-testid="jr-generate-ia-btn"
                  title="Gera missão, responsabilidades, requisitos técnicos e competências comportamentais via GPT-5.5"
                >
                  {iaLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {iaLoading ? "Gerando..." : "Gerar descrição com IA"}
                </button>
              </div>
              <Area label="Principais atividades" v={form.atividades?.principais_atividades} s={(x) => setForm({ ...form, atividades: { ...form.atividades, principais_atividades: x } })} full rows={4} />
              <Area label="Responsabilidades" v={form.atividades?.responsabilidades} s={(x) => setForm({ ...form, atividades: { ...form.atividades, responsabilidades: x } })} full rows={3} />
              <Area label="Rotinas da função" v={form.atividades?.rotinas} s={(x) => setForm({ ...form, atividades: { ...form.atividades, rotinas: x } })} full rows={3} />
              <Area label="Entregas esperadas" v={form.atividades?.entregas_esperadas} s={(x) => setForm({ ...form, atividades: { ...form.atividades, entregas_esperadas: x } })} full rows={3} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Area label="Limites de atuação" v={form.atividades?.limites_atuacao} s={(x) => setForm({ ...form, atividades: { ...form.atividades, limites_atuacao: x } })} rows={2} />
                <Area label="Relacionamento com outros setores" v={form.atividades?.relacionamento_setores} s={(x) => setForm({ ...form, atividades: { ...form.atividades, relacionamento_setores: x } })} rows={2} />
                <Area label="Máquinas, sistemas e ferramentas" v={form.atividades?.ferramentas_sistemas} s={(x) => setForm({ ...form, atividades: { ...form.atividades, ferramentas_sistemas: x } })} rows={2} />
                <Area label="Segurança, qualidade e produtividade" v={form.atividades?.seguranca_qualidade} s={(x) => setForm({ ...form, atividades: { ...form.atividades, seguranca_qualidade: x } })} rows={2} />
              </div>
            </TabsContent>

            <TabsContent value="historico" className="mt-4 space-y-3">
              <SectionHint>Registro de alterações auditadas neste cargo.</SectionHint>
              <div className="rounded-md border border-border max-h-[400px] overflow-y-auto">
                {history.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-[13px]">
                    Nenhum histórico ainda — o histórico é criado a cada alteração salva.
                  </div>
                ) : history.map((h, i) => (
                  <div key={h.id || i} className="p-3 border-b last:border-0 border-border/60">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <Badge variant="outline" className="text-[10px]">{h.action}</Badge>
                      <span>{fmtDateTime(h.at)}</span>
                      {h.user_name && <span>· {h.user_name}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} disabled={saving || !canEdit} data-testid="jr-save-btn">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando..." : "Salvar cargo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Area({ label, v, s, rows = 1, full, placeholder }) {
  const Component = rows > 1 ? Textarea : Input;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
      <Component value={v || ""} onChange={(e) => s(e.target.value)} placeholder={placeholder} {...(rows > 1 ? { rows } : {})} />
    </div>
  );
}

function SectionHint({ children }) {
  return (
    <div className="rounded-md border border-[#E8D9AE] bg-[#F9F4E7] px-3 py-2 text-[12.5px] text-[#5C4320]">
      {children}
    </div>
  );
}

function SwitchRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 px-3 py-1.5 rounded bg-white border border-border hover:border-[#C5A059]/50 cursor-pointer">
      <span className="text-[13px]">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function K({ label, value, icon: Icon, tone = "default" }) {
  const tones = {
    default: "bg-white", primary: "bg-[#234935] text-white",
    success: "bg-emerald-50", warning: "bg-amber-50", accent: "bg-[#F9F4E7]",
  };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  return (
    <div className={`rounded-lg border border-border ${tones[tone]} p-4`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${labelColor}`}>{label}</div>
          <div className={`mt-1 font-display text-[22px] font-semibold truncate ${valueColor}`}>{value}</div>
        </div>
        <Icon className={`h-4 w-4 shrink-0 ${tone === "primary" ? "text-[#C5A059]" : "text-muted-foreground"}`} />
      </div>
    </div>
  );
}
